import {
  ApiError,
  ContractKind,
  DeltaAction,
  DeltaStatus,
  ErrorCode,
  MovementStage,
  NotificationEvent,
  isIdentityMismatch,
  registryValues,
  type DeltaResponse,
  type DocumentResponse,
  type ContractResponse,
  type InsuredOnDateResponse,
  type MovementStatusListResponse,
  type MovementStatusResponse,
  type NotificationResponse,
} from '../../api';
import { FIELD_REJECTED, fieldErrors } from '../../components';
import { formatCpf, stripCpf } from '../../format';
import { ACTION_LABELS } from '../shared/roster';
import { identityMismatchFieldMessage, identityMismatchMessage } from './identityMismatch';

// Lifted to `areas/shared/roster.ts` for the staff company page (#417), and
// re-exported so the portal's screens and tests keep reading them from here.
export {
  ACTION_LABELS,
  isScheduled,
  matchesSearch,
  primaryScheduledChange,
  scheduledByCpf,
  scheduledChanges,
  searchRoster,
} from '../shared/roster';

/**
 * What the customer portal says, and the handful of rules it applies to what
 * the API returns.
 *
 * Kept out of the components so each rule can be tested as arithmetic rather
 * than through a rendered tree, and so the two screens cannot disagree about
 * what "agendada" means.
 */

export const PORTAL_COPY = {
  rosterTitle: 'Beneficiários',
  rosterDescription: 'Quem está coberto pelo contrato da sua empresa hoje.',
  // Distinct from the page title on purpose: two headings reading
  // "Beneficiários" is two things for a screen reader to land on and one thing
  // for a person to tell apart.
  rosterPanelTitle: 'Pessoas cobertas hoje',
  changesTitle: 'Movimentações',
  changesDescription:
    'Peça uma inclusão, uma exclusão, uma alteração salarial ou um afastamento. Cada pedido trata de uma coisa só.',
  noContract: 'Sua empresa ainda não tem um contrato cadastrado. Fale com a vyttech para começar.',
  searchLabel: 'Buscar por nome ou CPF',
  searchHint: 'Pode digitar parte do nome ou os números do CPF, com ou sem pontuação.',
  noMatches: 'Nenhum beneficiário corresponde à busca.',
  noMatchesHint: 'Confira a grafia do nome ou os números do CPF.',
  emptyRoster: 'Nenhuma pessoa coberta nesta data',
  emptyRosterHint:
    'Assim que a primeira inclusão for aprovada, ela aparece aqui. Veja as movimentações pendentes abaixo.',
  scheduledTitle: 'Movimentações agendadas',
  scheduledDescription:
    'Já pedidas e ainda por acontecer, inclusive em meses à frente. Estão aqui para você não pedir a mesma coisa duas vezes.',
  noScheduled: 'Nenhuma movimentação agendada.',
  scheduledUnavailable:
    'Não foi possível carregar as movimentações agendadas, então esta lista pode não estar marcando quem já tem algo pedido. Recarregue antes de pedir uma movimentação.',
  pendingTitle: 'Movimentações pendentes',
  pendingDescription:
    'Aguardando análise da vyttech. Uma movimentação pendente pode ser cancelada; não existe edição — cancele e peça de novo.',
  noPending: 'Nenhuma movimentação pendente.',
  historyTitle: 'Histórico de movimentações',
  historyDescription:
    'Tudo o que já foi pedido para este contrato, do mais recente ao mais antigo.',
  noHistory: 'Nenhuma movimentação registrada ainda.',
  newChangeTitle: 'Nova movimentação',
  actionLabel: 'O que você quer fazer',
  actionHint: 'Uma movimentação por pedido. Para duas coisas, envie dois pedidos.',
  actionPlaceholder: 'Escolha uma opção',
  personLabel: 'Pessoa',
  personHint: 'Escolha quem está no cadastro. Não é preciso redigitar os dados dela.',
  personPlaceholder: 'Escolha a pessoa',
  effectiveDateLabel: 'Data de vigência',
  effectiveDateHintAdmission: 'Primeiro dia de cobertura. Pode ser em um mês à frente.',
  effectiveDateHintTermination: 'Último dia de cobertura, inclusive. Pode ser em um mês à frente.',
  effectiveDateHintSalary: 'A partir de quando o novo salário vale. Pode ser em um mês à frente.',
  effectiveDateHintLeave: 'Primeiro dia de afastamento. Pode ser em um mês à frente.',
  leaveEndDateLabel: 'Último dia de afastamento (opcional)',
  // What an afastamento with no end date says in a table cell. A word rather
  // than a dash: "we do not know when they return" is a fact the customer acts
  // on, and a dash would read as "this column does not apply here".
  leaveOpenEnded: 'Em aberto',
  leaveEndDateHint:
    'Se já souber quando a pessoa volta, informe o último dia de afastamento. Se ainda não souber, deixe em branco e peça depois — a pessoa continua coberta durante todo o afastamento.',
  cpfLabel: 'CPF',
  cpfHint: 'Onze dígitos. A pontuação é colocada sozinha.',
  fullNameLabel: 'Nome completo',
  birthDateLabel: 'Data de nascimento',
  salaryLabel: 'Salário mensal',
  salaryHint: 'Em reais, com vírgula nos centavos. Ex.: 4.250,00',
  documentLabel: 'Documento assinado (opcional)',
  documentHint: 'O termo de rescisão assinado, em PDF ou foto. Fica anexado a esta movimentação.',
  submit: 'Enviar pedido',
  submitting: 'Enviando…',
  submitted: 'Pedido enviado. Ele aparece abaixo como pendente.',
  documentAttachFailed:
    'A movimentação foi registrada, mas o documento não pôde ser anexado. Cancele o pedido e envie de novo com o documento.',
  cancel: 'Cancelar pedido',
  cancelling: 'Cancelando…',
  cancelled: 'Pedido cancelado.',
  cancelFailed: 'Não foi possível cancelar este pedido.',
  statusTitle: 'Acompanhamento',
  statusDescription:
    'Onde está cada pedido que você fez. É esta tela que responde \u201cjá entrou no arquivo do mês?\u201d sem precisar perguntar.',
  statusPanelTitle: 'Seus pedidos',
  statusPanelDescription:
    'Do mais recente ao mais antigo. Nada fica de fora: o que foi recusado e o que foi cancelado também está aqui.',
  noMovements: 'Nenhum pedido registrado ainda.',
  noMovementsHint: 'Assim que você pedir uma movimentação, ela aparece aqui com a situação dela.',
  statusSummaryTitle: 'Resumo',
  cycleColumn: 'Entra no arquivo de',
  cutoffColumn: 'Data de corte',
  needsYouTitle: 'Esperando por você',
  needsYouDescription:
    'Estes pedidos foram devolvidos para correção. Envie cada um de novo com o ajuste pedido — eles não seguem sozinhos.',
  noticesTitle: 'Avisos',
  noticesDescription:
    'O que a vyttech fez com os seus pedidos, sem você precisar perguntar. O que é urgente também vai por e-mail.',
  noNotices: 'Nenhum aviso por enquanto.',
  noNoticesHint:
    'Quando um pedido for analisado ou o fechamento do mês sair, o aviso aparece aqui.',
  markRead: 'Marcar como lido',
  markAllRead: 'Marcar todos como lidos',
  markAllReadPending: 'Marcando…',
  markReadFailed: 'Não foi possível marcar como lido.',
  unreadBadge: 'não lido(s)',
  personHistoryTitle: 'Histórico da pessoa',
  personNotFound: 'Esta pessoa não está no cadastro da sua empresa.',
  personNotFoundHint:
    'Ou ela nunca esteve neste contrato, ou o endereço aponta para outra empresa. Volte à lista de beneficiários.',
  backToRoster: 'Voltar aos beneficiários',

  // --- The overview -------------------------------------------------------
  //
  // One sentence per panel, each answering a question somebody would otherwise
  // open a screen to find out. The panels carry counts and a link; the words
  // below are what makes a count mean something.
  overviewCoveredTitle: 'Pessoas cobertas hoje',
  overviewCoveredDescription:
    'Quantas pessoas o contrato cobre nesta data. A lista com nomes e CPF está em Beneficiários.',
  overviewCoveredMetric: 'Pessoas cobertas',
  overviewCoveredEmpty: 'Nenhuma pessoa coberta hoje',
  overviewCoveredEmptyHint:
    'Assim que a primeira inclusão for aprovada, ela aparece aqui. As movimentações já pedidas estão no painel abaixo.',
  overviewCoveredLink: 'Ver os beneficiários',

  overviewChangesTitle: 'Movimentações em aberto',
  overviewChangesDescription:
    'O que já foi pedido e ainda não terminou: o que espera análise e o que já foi aprovado para uma data à frente.',
  overviewPendingMetric: 'Aguardando análise',
  overviewAheadMetric: 'Aprovadas para os próximos meses',
  overviewNextChange: 'A próxima é',
  overviewNoChanges:
    'Nada em aberto. Nenhum pedido esperando análise nem agendado para os próximos meses.',
  overviewChangesLink: 'Pedir uma movimentação',
  overviewChangesStatusLink: 'Acompanhar os pedidos',

  overviewCycleTitle: 'Fechamento do mês',
  overviewCycleDescription:
    'O fechamento que está sendo trabalhado agora, a data em que ele lê o cadastro e se a vyttech já o assinou.',
  overviewCycleMonth: 'Mês',
  overviewCycleCutoff: 'Data de corte',
  overviewCycleSignOff: 'Assinatura',
  overviewCycleSignedOff: 'Assinado — o arquivo deste mês já está fechado.',
  overviewCycleOpen: 'Ainda não assinado — dá tempo de pedir uma movimentação para este mês.',
  overviewCycleUnknownCutoff:
    'Ainda não há nenhum pedido neste fechamento, então ele não tem data de corte a mostrar aqui.',
  overviewCycleLink: 'Ver o acompanhamento',

  overviewNoticesTitle: 'Avisos',
  overviewNoticesDescription:
    'O que a vyttech fez com os seus pedidos, sem você precisar perguntar.',
  overviewUnreadMetric: 'Avisos não lidos',
  overviewAllRead: 'Nenhum aviso não lido.',
  overviewNoticesLink: 'Ler os avisos',
} as const;

/** What each action does, in the words an HR person would use for it. */
export const ACTION_DESCRIPTIONS: Record<DeltaAction, string> = {
  [DeltaAction.ADMISSION]: 'Incluir uma pessoa no plano',
  [DeltaAction.TERMINATION]: 'Excluir uma pessoa do plano',
  [DeltaAction.SALARY_CHANGE]: 'Alterar o salário de uma pessoa',
  [DeltaAction.LEAVE]: 'Registrar o afastamento de uma pessoa',
};

export const STATUS_LABELS: Record<DeltaStatus, string> = {
  [DeltaStatus.SUBMITTED]: 'Pendente',
  [DeltaStatus.APPROVED]: 'Aprovada',
  [DeltaStatus.REJECTED]: 'Recusada',
  [DeltaStatus.RETURNED]: 'Devolvida para correção',
  [DeltaStatus.CANCELLED]: 'Cancelada',
};

export const EFFECTIVE_DATE_HINTS: Record<DeltaAction, string> = {
  [DeltaAction.ADMISSION]: PORTAL_COPY.effectiveDateHintAdmission,
  [DeltaAction.TERMINATION]: PORTAL_COPY.effectiveDateHintTermination,
  [DeltaAction.SALARY_CHANGE]: PORTAL_COPY.effectiveDateHintSalary,
  [DeltaAction.LEAVE]: PORTAL_COPY.effectiveDateHintLeave,
};

/**
 * Who a change is about, in words.
 *
 * Only an admission carries a name: the other two actions name a person the
 * roster already knows, so #210 does not repeat their identity on the delta.
 * A table of movements showing nothing but a CPF is a table nobody can read, so
 * the name is looked up in the roster that is already on screen -- and falls
 * back to the formatted CPF for somebody no longer in today's projection, which
 * is exactly what an approved termination leaves behind.
 */
export function nameForChange(
  delta: DeltaResponse,
  roster: readonly InsuredOnDateResponse[],
): string {
  if (delta.full_name !== null && delta.full_name !== undefined && delta.full_name !== '') {
    return delta.full_name;
  }
  const digits = stripCpf(delta.cpf);
  const person = roster.find((entry) => stripCpf(entry.cpf) === digits);
  return person?.full_name ?? formatCpf(delta.cpf);
}

// --- Paperwork --------------------------------------------------------------

/** Which documents are attached to each change right now, keyed by delta. */
export function documentsByDelta(
  documents: readonly DocumentResponse[],
): ReadonlyMap<string, readonly DocumentResponse[]> {
  const byDelta = new Map<string, DocumentResponse[]>();
  for (const document of documents) {
    for (const deltaId of document.linked_delta_ids ?? []) {
      const existing = byDelta.get(deltaId);
      if (existing === undefined) {
        byDelta.set(deltaId, [document]);
      } else {
        existing.push(document);
      }
    }
  }
  return byDelta;
}

// --- Where a request stands -------------------------------------------------

/**
 * The eight stages, in the words the person who filed the movement would use.
 *
 * These are the labels and {@link STAGE_DESCRIPTIONS} is the sentence under
 * each one. They are separate because a table cell has room for a word and a
 * summary has room for a sentence, and writing the word twice is how the two
 * come to disagree.
 */
export const STAGE_LABELS: Record<MovementStage, string> = {
  [MovementStage.RECEIVED]: 'Recebida',
  [MovementStage.FLAGGED]: 'Em análise',
  [MovementStage.SCHEDULED]: 'Agendada',
  [MovementStage.APPROVED]: 'Aprovada',
  [MovementStage.INCLUDED]: 'No arquivo enviado',
  [MovementStage.REJECTED]: 'Recusada',
  [MovementStage.RETURNED]: 'Devolvida para correção',
  [MovementStage.CANCELLED]: 'Cancelada',
};

/** What each stage means, and — where there is one — what happens next. */
export const STAGE_DESCRIPTIONS: Record<MovementStage, string> = {
  [MovementStage.RECEIVED]: 'Recebemos o pedido e ele entra no fechamento deste mês.',
  [MovementStage.FLAGGED]:
    'A vigência cai em um mês cujo arquivo já foi enviado à operadora, então alguém precisa olhar antes. Você não precisa reenviar.',
  [MovementStage.SCHEDULED]:
    'É para um mês à frente e está guardada até lá. Não está atrasada e nada depende de você.',
  [MovementStage.APPROVED]:
    'Aprovada e já valendo no cadastro a partir da vigência. O arquivo do mês ainda não foi fechado.',
  [MovementStage.INCLUDED]: 'Está no fechamento aprovado que vai para a operadora.',
  [MovementStage.REJECTED]: 'Recusada. O motivo está na linha do pedido.',
  [MovementStage.RETURNED]: 'Devolvida para correção: envie o pedido de novo com o ajuste.',
  [MovementStage.CANCELLED]: 'Cancelada por quem pediu.',
};

/**
 * The order the summary reads in: what is on its way, then how things ended.
 *
 * Not the enumeration's own order and not alphabetical. Somebody opening this
 * screen is asking about the things still moving, so those come first.
 */
export const STAGE_ORDER: readonly MovementStage[] = [
  MovementStage.RECEIVED,
  MovementStage.FLAGGED,
  MovementStage.SCHEDULED,
  MovementStage.APPROVED,
  MovementStage.INCLUDED,
  MovementStage.RETURNED,
  MovementStage.REJECTED,
  MovementStage.CANCELLED,
];

/**
 * The movements whose next move is the customer's.
 *
 * The API says so per row rather than leaving the screen to infer it from the
 * stage, so this filters on the flag: a stage added later that also waits on the
 * customer lands in this list without anybody remembering to add it here.
 */
export function awaitingCustomer(
  items: readonly MovementStatusResponse[],
): readonly MovementStatusResponse[] {
  return items.filter((item) => item.needs_customer_action);
}

// --- What we have told them -------------------------------------------------

/** Each kind of news, as one line. The API carries references and no prose. */
export const EVENT_LABELS: Record<NotificationEvent, string> = {
  [NotificationEvent.MOVEMENT_FLAGGED]: 'Um pedido com vigência retroativa entrou em análise',
  [NotificationEvent.MOVEMENT_APPROVED]: 'Um pedido foi aprovado',
  [NotificationEvent.MOVEMENT_REJECTED]: 'Um pedido foi recusado',
  [NotificationEvent.MOVEMENT_RETURNED]: 'Um pedido foi devolvido para correção',
  [NotificationEvent.MOVEMENT_DATE_CORRECTED]: 'A vigência de um pedido foi corrigida',
  [NotificationEvent.CYCLE_APPROVED]: 'O fechamento do mês foi aprovado',
  [NotificationEvent.CYCLE_REOPENED]: 'O fechamento do mês foi reaberto',
};

/**
 * Who a movement is about, as one cell.
 *
 * Only an admission carries a name -- the other two actions name somebody the
 * roster already knows -- so this falls back to the formatted CPF rather than
 * leaving a blank. It deliberately does not reach for the roster to resolve the
 * rest: the status screen is a listing, and a second full projection to put a
 * name on a cancelled movement from March is not worth the round trip.
 */
export function movementPerson(movement: MovementStatusResponse): string {
  const name = movement.delta.full_name;
  return name !== null && name !== undefined && name !== '' ? name : formatCpf(movement.delta.cpf);
}

/**
 * What one notice is about, in words, given the movements already on screen.
 *
 * A cycle notice names its month and how many movements it carried; a movement
 * notice names the movement, looked up in the listing the same screen is
 * showing, and identifies it exactly as the table beside it does. A notice whose
 * movement is not on this page says nothing rather than guessing -- the
 * alternative is a line reading "—" that looks like a rendering fault.
 */
export function noticeSubject(
  notification: NotificationResponse,
  movements: readonly MovementStatusResponse[],
): string | null {
  if (notification.cycle_month !== null && notification.cycle_month !== undefined) {
    const count = notification.movement_count ?? 0;
    return `${notification.cycle_month} — ${count} movimentação(ões)`;
  }
  const match = movements.find((item) => item.delta.id === notification.delta_id);
  if (match === undefined) {
    return null;
  }
  return `${ACTION_LABELS[match.delta.action]} — ${movementPerson(match)}`;
}

// --- Field-level refusals ---------------------------------------------------

/**
 * A pt-BR sentence per field the server can reject, so a refusal lands on the
 * input that caused it.
 *
 * The API names the offending field in `details[].field` -- `body.delta.cpf`
 * for a request-validation failure, `cpf` for a refusal from the delta layer
 * itself -- and both end in the same segment, which is what
 * {@link fieldErrors} keys on. What is *not* carried across is the server's
 * prose: it is written in English for whoever is integrating, and a form with
 * one English sentence on it is a form somebody mistrusts.
 */
const FIELD_MESSAGES: Record<string, string> = {
  cpf: 'Confira o CPF. Os onze dígitos precisam fechar com os dígitos verificadores.',
  effective_date: 'Confira a data de vigência.',
  full_name: 'Informe o nome completo da pessoa.',
  birth_date: 'Confira a data de nascimento.',
  salary: 'Confira o salário. Use vírgula para os centavos.',
  leave_end_date:
    'O último dia de afastamento não pode ser antes do primeiro. Deixe em branco se ainda não souber quando a pessoa volta.',
};

/**
 * What the roster refused, said in terms of the roster rather than of a field.
 *
 * A `409` from #210 means the change does not fit the population on its
 * effective date -- an admission for somebody already covered then, a
 * termination for somebody who is not. The field at fault is the date, and
 * "confira a data" alone would send the person to check a date that is very
 * probably right.
 */
const CONFLICT_MESSAGES: Record<string, string> = {
  effective_date:
    'Esta movimentação não cabe no cadastro nesta data. Confira se a pessoa já está — ou ainda não está — coberta nesse dia.',
  cpf: 'Esta pessoa não está no cadastro desta empresa nesta data.',
};

/**
 * The server's refusal, mapped onto the form's own fields.
 *
 * Anything the server named and this form does not show is dropped rather than
 * shown against the wrong input; it stays on the {@link ApiError} and in the
 * sentence above the form, so nothing is lost.
 */
export function changeFieldErrors(error: unknown): Record<string, string> {
  const rejected = fieldErrors(error);
  const isConflict = error instanceof ApiError && error.code === ErrorCode.CONFLICT;
  const registry = registryValues(error);
  const messages: Record<string, string> = {};

  for (const field of Object.keys(rejected)) {
    if (isIdentityMismatch(error)) {
      messages[field] = identityMismatchFieldMessage(field, registry[field]);
      continue;
    }
    const specific = isConflict ? CONFLICT_MESSAGES[field] : undefined;
    messages[field] = specific ?? FIELD_MESSAGES[field] ?? FIELD_REJECTED;
  }
  return messages;
}

/**
 * The sentence above the form, when the per-code copy would be too general.
 *
 * `identity_mismatch` is that case and, today, the only one. The generic
 * sentence for the code has to hold wherever the code can appear, so it can
 * only say the data does not agree with the registry -- while this form knows
 * it collects the name and the birth date and has, in `details[].registry_value`,
 * what the Receita Federal holds for each. Saying "a Receita Federal tem
 * registrado ANA MARIA SILVA para este CPF" is the difference between a person
 * fixing the inclusão and filing it again unchanged.
 *
 * `undefined` for everything else, which leaves {@link ApiError.message} in
 * place rather than replacing it with something vaguer.
 */
export function changeFormMessage(error: unknown): string | undefined {
  return isIdentityMismatch(error) ? identityMismatchMessage(registryValues(error)) : undefined;
}

// --- The overview -----------------------------------------------------------

/**
 * One contract, in the few words the overview has room for.
 *
 * Only shown when the company has more than one: a single-contract company --
 * which is every company today -- reads "Pessoas cobertas hoje" and knows
 * exactly which contract that is, and a panel title carrying an insurer name it
 * never had to choose between is noise.
 */
export function contractLabel(contract: ContractResponse): string {
  const kind = CONTRACT_KIND_WORDS[contract.kind] ?? contract.kind;
  return `${contract.insurer.name} · ${kind} · subestipulante ${contract.subestipulante}`;
}

/**
 * What insurance a contract is, in the words the staff screens use
 * (`KIND_LABELS` in the staff onboarding module). Named on the label because one
 * person can be under a vida and a saúde contract of the same company, and the
 * insurer alone does not always say which is which.
 */
const CONTRACT_KIND_WORDS: Record<ContractKind, string> = {
  [ContractKind.VIDA]: 'Vida',
  [ContractKind.SAUDE]: 'Saúde',
};

/**
 * The cutoff being worked on, as the overview states it.
 *
 * Three facts, and only the month comes off the response directly. The cutoff
 * date and the sign-off are properties of a *cycle*, and the API carries them on
 * the movements that land in one -- so they are read off a movement in the
 * current month rather than worked out here from `cutoff_day`. That is the same
 * rule the status screen follows: a portal that derived a cutoff for itself
 * would eventually disagree with the back-office about which month a change
 * lands in, which is the one thing this product cannot afford to do.
 *
 * Both are `null` when the current cycle has no movements on the page. That is
 * an honest "we cannot say from this answer", not a zero: the response carries
 * no cutoff of its own, and inventing one from the contract would be the
 * derivation this avoids.
 */
export interface CurrentCycle {
  /** The month being worked on, `YYYY-MM`, straight off the response. */
  readonly month: string;
  readonly cutoffDate: string | null;
  readonly isSignedOff: boolean | null;
}

export function currentCycle(status: MovementStatusListResponse): CurrentCycle {
  // Only a movement whose own cycle is the current one speaks for it. A
  // movement held for November carries November's cutoff and November's
  // sign-off, and reading the first row whatever its month is how a page comes
  // to announce next month's cutoff as this month's.
  const onCycle = status.items.find((item) => item.cycle_month === status.cycle_month);
  return {
    month: status.cycle_month,
    cutoffDate: onCycle?.cutoff_date ?? null,
    isSignedOff: onCycle?.cycle_is_signed_off ?? null,
  };
}

/**
 * Approved changes whose date has not arrived yet, soonest first.
 *
 * The listing is asked for `effective_from` today, which includes today -- and
 * a change effective today has already happened as far as the roster is
 * concerned, so it belongs in the count of people covered rather than in the
 * count of what is still coming. Same boundary as {@link isScheduled}, on
 * purpose: the two panels would otherwise disagree about one day.
 */
export function approvedAhead(
  deltas: readonly DeltaResponse[],
  today: string,
): readonly DeltaResponse[] {
  return deltas
    .filter((delta) => delta.status === DeltaStatus.APPROVED && delta.effective_date > today)
    .toSorted((a, b) => a.effective_date.localeCompare(b.effective_date));
}
